import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** right-aligned footer actions (ghost then primary) */
  footer?: ReactNode;
  /** max width of the panel; default 560px (ctAS §6.5) */
  width?: number;
  /** block scrim/Escape close while a submit is in flight */
  busy?: boolean;
}

/* ctAS §6.5 — the one true overlay. Scrim + centered panel, close ×,
   Escape to close, right-aligned footer. */
export default function Modal({ open, onClose, title, children, footer, width = 560, busy }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  // Portalled to <body>: several callers (QuickAdd inside the ticket form)
  // render a Modal from inside a <form>, and keeping the panel in that subtree
  // means its buttons and Enter presses are form controls of the host form.
  return createPortal(
    <div
      className="scpa-scrim"
      role="dialog"
      aria-modal="true"
      onClick={() => { if (!busy) onClose(); }}
    >
      <div className="scpa-modal" style={{ width: `min(${width}px, 100%)` }} onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-2">
            {title ? <h3 className="section-title">{title}</h3> : <span />}
            <button
              type="button"
              onClick={() => { if (!busy) onClose(); }}
              className="text-muted hover:text-fg shrink-0"
              aria-label="Close"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        <div>{children}</div>
        {footer && <div className="flex justify-end gap-2 pt-4">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
